import { createSelector, createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { useAppSelector } from "../../app/hooks";
import { BOTH_GOOD_AND_BAD_WEATHER, GLACIAL_DATA_STRING, SurfaceEnum } from "../../components/UserVisibleStrings";
import { ForecastTime, getSurfaceEnumFromString } from "../../app/forecastSurface";

import {
    addPendingToWindow,
    CAN_LOAD_ALL_TAUS,
    clear,
    forecastsAreEqual,
    getCurrentGeoJsonIndex,
    getCurrentOverallIndex,
    getForecastWindDataForSelectedTau,
    ImpactWindow,
    push,
    WindDirectionWindow,
    WINDOW_SIZE
} from "./windowUtils";
import { ForecastSurfacePage, ImpactSurfaceResponse, RequestForecastPayload } from "../../services/riskSurface";


// Define a type for the slice state
export interface SurfaceState
{
    window: ImpactWindow;
    windDirectionWindow: WindDirectionWindow;
    latestForecastTime: ForecastTime | undefined;
    allTaus: Array<number>;
    tausWithGeoJson: Array<number>;
    selectedChipName: string;
    incompleteTauCount: number; // the number of forecasts which have failed to load when a surface was queried
    shouldStopFetchingData: boolean;
    shouldAutoRefreshWindow: boolean;
    incompleteTauLatestError: string | undefined;
    selectedTime: number;
    countLastTau: number;
}

interface TauUpdate
{
    allTaus: Array<number>;
    tausWithGeoJson: Array<number>;
    newForecastTime: ForecastTime | undefined;
}

// Define the initial state using that type
const initialState: SurfaceState = {
    window: { cachedEntries: [], pendingQueries: [], forecastTime: undefined },
    windDirectionWindow: { taus: [], forecastTime: undefined },
    latestForecastTime: undefined,
    allTaus: [],
    tausWithGeoJson: [],
    selectedChipName: BOTH_GOOD_AND_BAD_WEATHER,
    shouldStopFetchingData: false,
    shouldAutoRefreshWindow: true,
    incompleteTauCount: 0,
    incompleteTauLatestError: undefined,
    selectedTime: Date.now(),
    countLastTau: 0,
};

type WrappedIncrement = {
    increment: number,
    min: number,
    max: number,
};
type DataUpdate = {
    surfaceData: Array<ImpactSurfaceResponse>,
    surfaceName: string,
    forecastTime: ForecastTime,
};
type PendingQuery = {
    page: ForecastSurfacePage | undefined,
    surfaceName: string,
    timestamp: number,
};

/**
 * In the event of a timed out tau request, we won't have the request data to know what failed,
 * so this is an approximation used because the follow on code requires this data type. Technically,
 * we can't guarantee that the start/limit values are correct, but if the request times out, we'd
 * like to try the request again.
 * @param state
 */
function getSurrogateForecastPayload( state: Draft<SurfaceState> ): RequestForecastPayload
{
    // then our current time failed to load
    return {
        surfaceName: getSurfaceName( state.selectedChipName ),
        surface: {
            start: getCurrentGeoJsonIndex( state.selectedTime, state.tausWithGeoJson ),
            limit: 1
        },
        time: { date: state.window.forecastTime?.date ?? "", hour: state.window.forecastTime?.hour ?? "" }
    };
}

export const surfaceSlice = createSlice( {
    name: "surface",
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        setTaus: ( state, action: PayloadAction<TauUpdate> ) =>
        {
            state.allTaus = action.payload.allTaus;
            state.tausWithGeoJson = action.payload.tausWithGeoJson;
        },
        setRouteTime: ( state, action: PayloadAction<ForecastTime> ) =>
        {
            clear( state, action.payload );
        },
        setWilmaTime: ( state, action: PayloadAction<ForecastTime> ) =>
        {
            // If you haven't already loaded a forecast OR you return from a route to WILMA,
            // update the forecast time
            state.latestForecastTime = action.payload;

            // If the forecast time has changed, we need to clear the cache and
            // re-query for the impact surfaces
            clear( state, action.payload );
        },
        addPending: ( state, action: PayloadAction<PendingQuery> ) =>
        {
            if ( action.payload.page === undefined )
            {
                return;
            }

            const indexes = [];
            for ( let i = 0; i < action.payload.page.limit; i++ )
            {
                indexes.push( { index: action.payload.page.start + i, timestamp: action.payload.timestamp } );
            }
            addPendingToWindow( state, indexes );
        },
        updateData: ( state, action:PayloadAction<DataUpdate> )=>
        {
            if ( getSurfaceEnumFromString( state.selectedChipName )?.toLowerCase() !== action.payload.surfaceName.toLowerCase() )
            {
                return;
            }
            if ( !forecastsAreEqual( state.window.forecastTime, action.payload.forecastTime ) )
            {
                return;
            }
            // We cannot assume that the ordering of the returned elements is preserved - checked the embedded timestamps for an accurate index
            const dataToAdd = action.payload.surfaceData.flatMap( each =>
            {
                const embeddedTimestampIndex = getCurrentGeoJsonIndex( each.impactUrls.tau, state.tausWithGeoJson );
                return {
                    index: embeddedTimestampIndex,
                    impactUrls: each.impactUrls
                };
            } );
            push( state, dataToAdd );
        },
        changeSurface: ( state, action: PayloadAction<string> ) =>
        {
            const chipName = action.payload;
            // this is the current surface before the user changed it
            if ( state.selectedChipName === chipName || state.window.forecastTime === undefined )
            {
                return; // do nothing if the surface is the same
            }
            const oldSurfaceName = getSurfaceName( state.selectedChipName );
            const newSurfaceName = getSurfaceName( chipName );
            state.selectedChipName = chipName;

            if ( oldSurfaceName !== newSurfaceName )
            {
                clear( state, state.window.forecastTime, false );
            }
        },
        resetSurface: ( state, action:PayloadAction<{ forecastTime: ForecastTime | undefined, tauTimeout: boolean }> ) =>
        {
            // Called when the wilma surface button is pressed or the "new" button
            // clear the "cached" surfaces
            if ( action.payload !== undefined )
            {
                clear( state, action.payload.forecastTime, !action.payload.tauTimeout );
                state.latestForecastTime = action.payload.forecastTime;
            }
            //called when a route is closed and the user returns to wilma
            else
            {
                clear( state, state.latestForecastTime );
            }
        },
        updateErrorMessage: ( state, action: PayloadAction<string> ) =>
        {
            state.incompleteTauLatestError = action.payload;
            if ( action.payload.includes( "glacial" ) || action.payload.includes( "Missing S3 key" ) || action.payload.includes( "Glacier" ) )
            /*
             * Potential causes of Missing s3 key
             *    The data was generated prior to our addition of goldilocks - this data has a different structure and just can't be fetched the same way.
             *    The backend failed to generate this file for whatever reason (e.g. permissions, lambda timed out, unhandled exception, etc.)
            */
            {
                state.shouldStopFetchingData = true;
                state.incompleteTauLatestError = GLACIAL_DATA_STRING;
            }
            else
            {
                state.incompleteTauLatestError = action.payload;
            }
        },
        clearTimedOutQueries: ( state ) =>
        {
            const currentTime = Date.now();
            state.window.pendingQueries = state.window.pendingQueries
                .filter( each => currentTime - each.timestamp < 14000 );
        },
        incrementIncompleteTauCount: ( state, action:PayloadAction<RequestForecastPayload | undefined> ) =>
        {
            const payload: RequestForecastPayload = action.payload ?? getSurrogateForecastPayload( state );

            if ( forecastsAreEqual( payload.time, state.window.forecastTime ) &&
             payload.surfaceName === getSurfaceEnumFromString( state.selectedChipName ) )
            {
                const failedTaus: Array<number> = [];
                for ( let i = payload.surface.start; i < payload.surface.limit; i++ )
                {
                    failedTaus.push( i );
                }
                state.incompleteTauCount = state.incompleteTauCount + failedTaus.length;
                state.window.pendingQueries = state.window.pendingQueries
                    .filter( each => !failedTaus.includes( each.index ) );
            }
        },
        setSelectedTime: ( state, action: PayloadAction<number> ) =>
        {
            state.selectedTime = action.payload;
        },
        incrementTimeWrapped: ( state, action: PayloadAction<WrappedIncrement> ) =>
        {
            const currTime = state.selectedTime;
            let nextTime = Math.min(
                currTime + action.payload.increment,
                action.payload.max
            );
            /* I don't love this, but during playback, we were spending a split second
               on the last tau and immediately jumping back to the beginning.  This isn't
               really that usable, so the logic below adds an artificial wait time for the
               last tau by counting how many frames (per our frame rate) we spend on the last tau.
             */
            if ( currTime === action.payload.max )
            {
                if ( state.countLastTau > 25 )
                {
                    nextTime = action.payload.min;
                    state.countLastTau = 0;
                }
                else
                {
                    state.countLastTau = state.countLastTau + 1;
                }
            }
            else if ( state.countLastTau > 0 )
            {
                state.countLastTau = 0;
            }
            state.selectedTime = nextTime;
        },
        setShouldAutoRefreshWindow( state, action: PayloadAction<boolean> )
        {
            state.shouldAutoRefreshWindow = action.payload;
        },
        setWindDirectionData( state, action: PayloadAction<WindDirectionWindow> )
        {
            state.windDirectionWindow = action.payload;
        },
    },
} );

export const { updateData,
    changeSurface,
    resetSurface,
    setWilmaTime,
    setRouteTime,
    incrementIncompleteTauCount,
    setTaus,
    setSelectedTime,
    incrementTimeWrapped,
    clearTimedOutQueries,
    addPending,
    updateErrorMessage,
    setShouldAutoRefreshWindow,
    setWindDirectionData, } = surfaceSlice.actions;

// Other code such as selectors can use the imported `RootState` type
const selectedTime = ( state: RootState ) => state.surface.selectedTime;
export const useSelectedTime = () => useAppSelector( selectedTime );
// Other code such as selectors can use the imported `RootState` type
export const useForecastTime = () => useAppSelector( ( state: RootState ) => state.surface.window.forecastTime );
const selectedChip = ( state:RootState )=> state.surface.selectedChipName;
export const useSelectedChip = () => useAppSelector( selectedChip );
function getSurfaceName( selectedChipName: string ): SurfaceEnum
{
    return getSurfaceEnumFromString( selectedChipName ) ?? SurfaceEnum.AGGREGATE;
}

const selectedSurface = createSelector( selectedChip, ( selectedChipName ) =>
{
    return getSurfaceName( selectedChipName );
} );
export const useSelectedSurface = () => useAppSelector( selectedSurface );
export const useIncompleteTauCount = () => useAppSelector( ( state: RootState ) => state.surface.incompleteTauCount );
const allTaus = ( state: RootState ) => state.surface.allTaus;
export const useAllTaus = () => useAppSelector( allTaus );

// This is the last tau in the current window
// use this when trying to find the end of the forecast
export const lastTau = ( state: RootState ) => state.surface.allTaus[state.surface.allTaus.length - 1];
export const useLastTau = () => useAppSelector( lastTau );

const stack = ( state: RootState ) => state.surface.window.cachedEntries;
const windDirectionStack = ( state: RootState ) => state.surface.windDirectionWindow.taus;
const tausWithGeoJson = ( state: RootState ) => state.surface.tausWithGeoJson;
export const useTausWithGeoJson = () => useAppSelector( tausWithGeoJson );
const selectPotentialTausIndex = createSelector(
    stack,
    ( state: RootState ) => state.surface.window.pendingQueries,
    ( ImpactResponseStack, pendingTausIndex ) => ImpactResponseStack
        .map( each => each.index )
        .concat( pendingTausIndex.map( indexandTimestamp => indexandTimestamp.index ) )
);
export const usePotentalTausIndex = () => useAppSelector( selectPotentialTausIndex );

const selectWindDirectionSurface = createSelector(
    windDirectionStack,
    selectedTime,
    allTaus,
    ( windDirectionData, currentlySelectedTime, allTausList ) =>
    {
        return getForecastWindDataForSelectedTau( currentlySelectedTime, allTausList, windDirectionData );
    }
);

const selectImpactSurface = createSelector(
    stack,
    selectedTime,
    tausWithGeoJson,
    allTaus,
    ( ImpactResponseStack, currentlySelectedTime, tausWithGeoJsonList, allTausList ) =>
    {
        const selectImpactSurfaceIndex = getCurrentOverallIndex( currentlySelectedTime, tausWithGeoJsonList, allTausList );
        const filtered = ImpactResponseStack.filter( each => each.index === selectImpactSurfaceIndex );
        return filtered.length < 1 ? undefined : filtered[0];
    }
);

export const useSelectedImpactSurface = () => useAppSelector( selectImpactSurface );
export const useSelectedWindDirectionSurface = () => useAppSelector( selectWindDirectionSurface );

export const isFirstWilmaLoad = () => useAppSelector( state => state.surface.latestForecastTime === undefined && state.surface.window.forecastTime === undefined );

const shouldStopFetchingData = ( state: RootState ) => state.surface.shouldStopFetchingData;
export const useShouldStopFetchingData = () => useAppSelector( shouldStopFetchingData );

const getLatestErrormessage = ( state: RootState ) => state.surface.incompleteTauLatestError;
export const useLatestErrorMessage = () => useAppSelector( getLatestErrormessage );

const tauMaximum = ( state: RootState ) => CAN_LOAD_ALL_TAUS ? state.surface.tausWithGeoJson.length : WINDOW_SIZE;
const cachedEntries = ( state: RootState ) => state.surface.window.cachedEntries.length;
const fractionLoaded = createSelector(
    tauMaximum,
    cachedEntries,
    ( maxTaus, entries ) => entries / maxTaus
);
export const useFractionLoaded = () => useAppSelector( fractionLoaded );

const shouldAutoRefreshWindow = ( state: RootState ) => state.surface.shouldAutoRefreshWindow;
export const useShouldAutoRefreshWindow = () => useAppSelector( shouldAutoRefreshWindow );

export const useWindDirectionTaus = (): Array<number> => useAppSelector( ( state: RootState ) => state.surface.windDirectionWindow.taus.map( t => t.tau ) );
